import { Injectable } from '@angular/core';
import { IApiKey } from '../api-key/api-key.component';
import { AuthenticationService } from './authentication.service';
import {HttpClient} from "@angular/common/http";
import {AngularFireDatabase} from "@angular/fire/database";
import {catchError, map} from "rxjs/operators";
import {Observable, throwError} from "rxjs";

export enum ZapierEventTypes {
  NewTest = 'new_test',
  UpdateTest = 'update_test',
  DeleteTest = 'delete_test',
}

export interface IZapierSubscription {
  hookUrl: string;
  eventType: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  // Production
  // baseUrl = 'https://us-central1-ebi-dev-e610c.cloudfunctions.net/api/v1/';
  //
  // Production Prod
  baseUrl = 'https://us-central1-ebi-prod.cloudfunctions.net/api/v1/';

  // Emulation
  // baseUrl = 'http://localhost:5001/ebi-dev-e610c/us-central1/api/v1/';

  // Emulation Prod
  // baseUrl = 'http://localhost:5001/ebi-prod/us-central1/api/v1/';

  constructor(private authenticationService: AuthenticationService,
              private http: HttpClient,
              private db: AngularFireDatabase) {
  }

  getItems(pageSize: number): Observable<any> {
    return this.db.list('testResults', ref => ref
      )
      .snapshotChanges().pipe(
        catchError(error => {
          console.error('Error getting items:', error);
          return throwError('Something went wrong, please try again later.');
        })
      );
  }

  async handleNoteChange($event: Event, testId: string, apiUid: string, idToken: string): Promise<void> {
    const target = $event.target as HTMLInputElement; // Specifying $event.target type to avoid weird typing error
    const data = {
      note: target.value,
      uid: apiUid
    };
    await fetch(this.baseUrl + 'tests/' + testId, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
  }

  async saveWaterSource(
    userApiData: {
      waterSources: {
        name: string, place_id: string, source_id: string
      },
      uid: string
    },
    apiUid: string, idToken: string): Promise<void> {
    userApiData.uid = apiUid;
    await fetch(this.baseUrl + 'users/' + apiUid, {
        method: 'PUT',
        body: JSON.stringify(userApiData),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
  }

  async updateUserDetails(data, uid: string, idToken: string) {
    await fetch(this.baseUrl + 'users/' + uid, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
  }

  async getTestData(filters, idToken: string) {
    const response = await fetch(this.baseUrl + 'tests', {
        method: 'POST',
        body: JSON.stringify(filters),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
    const testData = await response.json();
    return testData;
  }

  async getTestDataPagination(filters, idToken: string) {
    const response = await fetch(this.baseUrl + 'pagination', {
        method: 'POST',
        body: JSON.stringify(filters),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
    const testData = await response.json();
    return testData;
  }

  async getTestDataSearchNote(filters, idToken: string) {
    const response = await fetch(this.baseUrl + 'note', {
        method: 'POST',
        body: JSON.stringify(filters),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
    const testData = await response.json();
    return testData;
  }

  async updateTestData(data, testId: string, apiUid: string, idToken: string): Promise<void> {
    if (!data.uid) {
      data.uid = apiUid;
    }
    console.log(data);
    await fetch(this.baseUrl + 'tests/' + testId, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
  }

  async saveUserLocation(data, apiUid: string, idToken: string): Promise<void> {
    await fetch(this.baseUrl + 'users/' + apiUid, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
  }

  async getUserData(apiUid: string, idToken: string) {
    const response = await fetch(this.baseUrl + 'user/' + apiUid, {
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + idToken
      }
    });
    return await response.json();
  }

  async deletePlace(data, apiUid: string, idToken: string): Promise<void> {
    await fetch(this.baseUrl + 'users/' +
      apiUid + '/places', {
        method: 'DELETE',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
  }

  async deleteWaterSource(data, apiUid: string, idToken: string): Promise<void> {
    await fetch(this.baseUrl + 'users/' +
      apiUid + '/water_sources', {
        method: 'DELETE',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + idToken
        }
      }
    );
  }

  async createUser(data) {
    const response = await fetch(this.baseUrl + 'users', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return await response.json();
  }

  async getAllUsers(idToken: string) {
    const res = await fetch(this.baseUrl + 'users', {
      method: 'GET',
      body: undefined,
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      }
    });
    return await res.json();
  }

  async getAllLocation(idToken: string) {
    const res = await fetch(this.baseUrl + 'location', {
      method: 'GET',
      body: undefined,
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      }
    });
    return await res.json();
  }

  async getOrgUsers(idToken: string, orgId: string) {
    console.log(idToken, orgId);
    const res = await fetch(this.baseUrl + 'users/' + orgId.toString(), {
      method: 'GET',
      body: undefined,
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      }
    });

    const json = await res.json();
    return json;

  }

  async getOrgData(idToken: string, orgId: string) {
    const res = await fetch(this.baseUrl + 'organizations/' + orgId.toString(), {
      method: 'GET',
      body: undefined,
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      }
    });
    const json = await res.json();
    return await json;

  }

  async getAllOrgs(idToken: string) {
    const res = await fetch(this.baseUrl + 'organizations', {
      method: 'GET',
      body: undefined,
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      }
    });
    const json = await res.json();
    return await json;
  }

  async updateOrgData(idToken: string, orgId: string, data) {
    const res = await fetch(this.baseUrl + 'organizations/' + orgId, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      }

    });
    return await res.json();
  }

  async updateUserOrgData(idToken: string, orgId: string, uid: string, userRole: string) {
    const res = await fetch(this.baseUrl + 'organizations/' + orgId + '/user/' + uid, {
      method: 'PUT',
      body: JSON.stringify({
        userRole,
        orgData: {
          orgId
        }
      }),
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      }

    });
    const json = await res.json();
    return json;

  }

  async getUserCustomClaims(idToken: string, uid: string) {
    try {
      const response = await fetch(this.baseUrl + 'user/' + uid + '/custom-claims', {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + idToken
        }
      });
      const json = await response.json();
      return json;
    } catch (e) {
      console.log(e);
    }
  }

  async sendEmail(emailData, idToken: string, email) {
    const apiData = emailData;

    apiData.to = apiData.to.length > 1 ? apiData.to.join(', ') : apiData.to[0];

    try {
      const response = await fetch(this.baseUrl + 'admin/email-test-data', {
        method: 'POST',
        body: JSON.stringify(apiData),
        headers: {
          Authorization: 'Bearer ' + idToken,
          'Content-Type': 'application/json'
        }
      });
      return await response.json();
    } catch (e) {
      console.log(e);
    }
  }


  async createTest(data: any, idToken: string) {
    try {
      const response = await fetch(this.baseUrl + 'tests', {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: {
          Authorization: 'Bearer ' + idToken,
          'Content-Type': 'application/json'
        }
      });
      return await response.json();

    } catch (e) {
      console.log(e);
    }
  }


  async cleanUsers(idToken: string, userArray: Array<any>) {
    for (const userData of userArray) {
      try {
        if (userData.customClaims && !userData.customClaims.orgData) {
          await this.updateUserOrgData(
            idToken,
            userData.customClaims.orgId,
            userData.uid,
            userData.customClaims.role ? userData.customClaims.role : userData.customClaims.userRole);
        } else if (!userData.customClaims) {
          await this.updateUserOrgData(
            idToken,
            '0',
            userData.uid,
            'user'
          );
        } else if (userData.customClaims && !userData.customClaims.orgData?.orgId) {
          await this.updateUserOrgData(
            idToken,
            '0',
            userData.uid,
            userData.customClaims.role ? userData.customClaims.role : userData.customClaims.userRole
          );
        }
      } catch (e) {
        console.log(e);
      }
    }
  }

  /**
   * generate a new api key
   * @param keyName - the name of the key
   * @param uid - user id
   * @param idToken - user id token
   * @returns api key
   */
  async generateApiKey(keyName: string, uid: string, idToken: string): Promise<IApiKey> {
    try {
      const response = await fetch(this.baseUrl + 'admin/generate-api-key', {
        method: 'POST',
        body: JSON.stringify({uid, keyName}),
        headers: {
          Authorization: 'Bearer ' + idToken,
          'Content-Type': 'application/json'
        }
      });
      // return the apikey property
      const json = await response.json();
      return json.apiKey;
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * update api key
   * @param apiKey - api key to update
   * @param uid - user id
   * @param idToken - id token
   */
  async updateApiKey(apiKey: IApiKey, uid: string, idToken: string): Promise<void> {
    // make new object from apiKey without lastUsedString, issuedDateString, and visible properties
    const apiKeyData = {
      name: apiKey.name,
      key: apiKey.key,
      activated: apiKey.activated,
      lastUsed: apiKey.lastUsed,
      issuedDate: apiKey.issuedDate,
    };


    await fetch(this.baseUrl + 'admin/update-api-key', {
      method: 'POST',
      body: JSON.stringify({apiKeyData, uid}),
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * Delete Api Key from user data
   * @param apiKey - api key to delete
   * @param uid - user id
   * @param idToken - id token
   */
  async deleteApiKey(apiKey: IApiKey, uid: string, idToken: string): Promise<void> {
    // delete lastUsedString, issuedDateString, and visible properties from apiKey
    delete apiKey.lastUsedString;
    delete apiKey.issuedDateString;
    delete apiKey.visible;

    await fetch(this.baseUrl + 'admin/delete-api-key', {
      method: 'POST',
      body: JSON.stringify({apiKey, uid}),
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      }
    });
  }
}
